import React, { useEffect, useMemo } from 'react';
import { Form } from 'antd';
import ProForm, { ProFormText } from '@ant-design/pro-form';
import { useSelector } from 'umi';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { BranchModel, ServiceModel } from '@/typings/models/Location';
import { getDynamicFormInputRules, getFormBlurProps } from '@/utils/form';
import { updateServiceRequest } from '@/services/api/location';
import { processAPIResponseNow } from '@/utils/request';
import FORM_TYPES from '@/constants/formTypes';

interface ServiceMetaProps {
  service: ServiceModel;
  branch: BranchModel;
  onSuccess?: (data?: any) => void;
}

const ServiceMeta: React.FC<ServiceMetaProps> = ({ service, branch, onSuccess }) => {
  const [form] = Form.useForm();

  const journeyMetaConfig = useSelector((state: DefaultRootState) =>
    get(state.journey, `config.${branch?.lead_source?.id}.calkit_meta`, []),
  );

  const metaToBeShown = useMemo(() => {
    return journeyMetaConfig || [];
  }, [journeyMetaConfig]);

  // Normalize meta to always be an object for the form
  const normalizedMeta = useMemo(() => {
    const meta = service?.calendar?.meta;
    if (Array.isArray(meta)) {
      return meta.reduce((acc, item) => ({ ...acc, ...item }), {});
    }
    return meta || {};
  }, [service?.calendar?.meta]);

  // Stable initial values for the same service to prevent random changes during typing
  const stableInitialValues = useMemo(() => normalizedMeta, [service?.id]);

  useEffect(() => {
    if (service?.id) {
      form.setFieldsValue(normalizedMeta);
      if (isEmpty(normalizedMeta)) {
        form.resetFields();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.id, form]);

  const handleUpdateMeta = async (name: string, value: any) => {
    const currentMetaValue = get(normalizedMeta, name);
    if (currentMetaValue === value) return;

    const newMeta = {
      ...(form.getFieldsValue() || {}),
      [name]: value,
    };

    if (value === undefined || value === null || value === '') {
      delete newMeta[name];
    }

    const payload = {
      branch_id: branch.id,
      lead_source_id: branch.lead_source?.id,
      address: branch.address,
      latitude: branch.latitude,
      longitude: branch.longitude,
      timezone: branch.timezone,
      calendar: {
        ...(service.calendar || {}),
        meta: newMeta,
      },
    };

    try {
      const response = await updateServiceRequest({
        params: { serviceId: service.id! },
        payload,
      });

      processAPIResponseNow(response, {
        onSuccess: (data) => {
          const updatedService = data?.data || {
            ...service,
            calendar: {
              ...(service.calendar || {}),
              meta: newMeta,
            },
          };


          console.log(updatedService, "ASB")
          onSuccess?.(updatedService);
        },
        onEnd: () => {
        },
      });
    } catch (error) {
    }
  };

  if (isEmpty(metaToBeShown)) return null;

  const getExtraFieldProps = (field: any) => {
    return {
      ...getFormBlurProps({
        type: FORM_TYPES.TEXT,
        callback: handleUpdateMeta,
        name: field.key || '',
      }),
      disabled: field.read_only,
    };
  };

  return (
    <ProForm
      form={form}
      submitter={false}
      initialValues={stableInitialValues}
    >
      {metaToBeShown.map((field: any) => (
        <ProFormText
          key={field.key}
          label={field.title || field.label || field.key}
          name={field.key}
          placeholder={field.placeHolder}
          tooltip={field.tooltip}
          getValueFromEvent={(e) => {
            const value = e?.target?.value;
            return isEmpty(value) ? undefined : value;
          }}
          rules={getDynamicFormInputRules(field)}
          {...getExtraFieldProps(field)}
        />
      ))}
    </ProForm>
  );
};

export default ServiceMeta;
